import { Col, DatePicker, Row, Tabs } from "antd";
import type { Dayjs } from "dayjs";
import { useState } from "react";
import CardCharts from "../../components/dashboard/CardCharts/CardCharts";
import CollapsibleTableCard from "../../components/dashboard/CollapsibleTableCard/CollapsibleTableCard";
import PageLayout from "../../components/layout/PageLayout/PageLayout";
import TableActions from "../../components/tables/TableActions/TableActions";
import TableEvents from "../../components/tables/TableEvents/TableEvents";
import TableConstats from "../../components/tables/TableConstats/TableConstats";
import {
  useFetchDashboardStatisticsQuery,
  useLazyFetchPaginatedDashboardActionsQuery,
  useLazyFetchPaginatedDashboardConstatsQuery,
  useLazyFetchPaginatedDashboardEventsQuery,
} from "../../features/api/apiSlice";
import { useCrudAction } from "../../features/api/crud/hooks/useCrudAction";
import styles from "./PageDashboard.module.scss";
import Br from "../../components/ui/Br";
import spacing from "../../components/ui/spacingConfig";
import { useCrudEvent } from "../../features/api/crud/hooks/useCrudEvent";
import { useCrudConstat } from "../../features/api/crud/hooks/useCrudConstat";
import CardEmptyState from "./components/CardEmptyState";

// The "count" pagination field isn't reliable on some endpoints;
// the actual rows returned ("results") are what determines emptiness.
const isEmptyResponse = (data: { results: unknown[] } | undefined): boolean => {
  if (!data) return false;
  return data.results.length === 0;
};

const PageDashboard: React.FC = () => {
  // Fetch events
  const [
    fetchPaginatedEvents,
    { data: eventsData, isFetching: isFetchingEvents },
  ] = useLazyFetchPaginatedDashboardEventsQuery();

  // Fetch actions
  const [
    fetchPaginatedActions,
    { data: actionsData, isFetching: isFetchingActions },
  ] = useLazyFetchPaginatedDashboardActionsQuery();

  // Fetch constats
  const [
    fetchPaginatedConstats,
    { data: constatsData, isFetching: isFetchingConstats },
  ] = useLazyFetchPaginatedDashboardConstatsQuery();

  const [dateRange, setDateRange] = useState<[string, string] | undefined>();

  // Fetch statistics
  const { data: statistics, isFetching: isFetchingStatistics } =
    useFetchDashboardStatisticsQuery(
      dateRange ? { startDate: dateRange[0], endDate: dateRange[1] } : undefined,
    );

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0].format("YYYY-MM-DD"), dates[1].format("YYYY-MM-DD")]);
    } else {
      setDateRange(undefined);
    }
  };

  // Event: delete
  const { onDelete: onDeleteEvent, isDeleting: isDeletingEvent } = useCrudEvent(
    {
      fetchEvents: fetchPaginatedEvents,
    },
  );

  // Action: delete
  const { onDelete: onDeleteAction, isDeleting: isDeletingAction } =
    useCrudAction(fetchPaginatedActions);

  // Constat: delete
  const { onDelete: onDeleteConstat, isDeleting: isDeletingConstat } =
    useCrudConstat({ fetchConstats: () => fetchPaginatedConstats() });

  const tabItems = [
    {
      key: "statistiques",
      label: "Statistiques",
      children: (
        <>
          <DatePicker.RangePicker
            format="DD/MM/YYYY"
            onChange={handleDateRangeChange}
            style={{ marginBottom: spacing.m }}
          />
          <Row gutter={spacing.l}>
          <CardCharts
            title="ProgrÃ¨s actions"
            statistics={statistics}
            type="action"
            isFetching={isFetchingStatistics}
          />
          <CardCharts
            title="ProgrÃ¨s Ã©vÃ©nement"
            statistics={statistics}
            type="event"
            isFetching={isFetchingStatistics}
          />
          </Row>
        </>
      ),
    },
    {
      key: "tableaux",
      label: "Actions / ÃvÃ©nements",
      children: (
        <>
          <Row>
            <Col span={spacing.m}>
              {isEmptyResponse(actionsData) ? (
                <CardEmptyState message="Aucune action Ã  traiter" />
              ) : (
                <CollapsibleTableCard
                  title="Actions Ã  faire"
                  count={actionsData?.results.length || 0}
                >
                  <TableActions
                    actionsData={actionsData}
                    fetchData={fetchPaginatedActions}
                    isLoading={isFetchingActions || isDeletingAction}
                    onDelete={onDeleteAction}
                    renderCard={false}
                  />
                </CollapsibleTableCard>
              )}
            </Col>
          </Row>
          <Br height={spacing.xl} />
          <Row>
            <Col span={spacing.m}>
              {isEmptyResponse(eventsData) ? (
                <CardEmptyState message="Aucun Ã©vÃ¨nement Ã  traiter" />
              ) : (
                <CollapsibleTableCard
                  title="ÃvÃ©nements Ã  traiter"
                  count={eventsData?.results.length || 0}
                >
                  <TableEvents
                    eventsData={eventsData}
                    fetchData={fetchPaginatedEvents}
                    isLoading={isFetchingEvents || isDeletingEvent}
                    onDelete={onDeleteEvent}
                    renderCard={false}
                  />
                </CollapsibleTableCard>
              )}
            </Col>
          </Row>
          <Br height={spacing.xl} />
          <Row>
            <Col span={spacing.m}>
              {isEmptyResponse(constatsData) ? (
                <CardEmptyState message="Aucun constat Ã  traiter" />
              ) : (
                <CollapsibleTableCard
                  title="Constats Ã  traiter"
                  count={constatsData?.results.length || 0}
                >
                  <TableConstats
                    data={constatsData}
                    fetchData={fetchPaginatedConstats}
                    isLoading={isFetchingConstats || isDeletingConstat}
                    onDelete={onDeleteConstat}
                  />
                </CollapsibleTableCard>
              )}
            </Col>
          </Row>
        </>
      ),
    },
  ];

  return (
    <PageLayout pageContainerClassName={styles.root}>
      <h1>Tableau de bord</h1>
      <Br height={spacing.sm} />
      <Tabs defaultActiveKey="tableaux" items={tabItems} />
    </PageLayout>
  );
};

export default PageDashboard;
